import React from 'react';
import Has from '../../../auth/Has';
import {
  CAN_DELETE_SCREENING_CLEARANCE_RULE_CONTENT,
  CAN_MODIFY_SCREENING_CLEARANCE_RULE_CONTENT,
} from '../../../auth/permissions';
import { Button, Dropdown } from 'react-bootstrap';
import { formatEnumLabel } from '../../../common/helper';
import { screeningClearanceRuleContentApi } from '../../../apis';
import { showFeedback } from '../../../feedback/feedbackReducer';
import { useDispatch } from 'react-redux';
import FormattedDateTime from '../../../common/datetime/FormattedDateTime';

import './ScreeningClearanceRuleContents.scss';
import {
  InternalApiEntityType,
  InternalApiScreeningClearanceRuleContent,
  InternalApiScreeningClearanceRuleContentStatusEnum,
} from '../../../api-internal';
import { DropdownWrapper } from '../../../common/DropdownWrapper';
import { useNavigate } from 'react-router';
import { showConfirmationModal } from '../../../confirmation-modal/confirmationModalReducer';
import { clearanceRuleClearancesDashboardLink } from '../../../dashboard/dashboardHelpers';

export const ContentInfo = ({
  content,
  ruleHandle,
  entityType,
  index,
  onComplete,
  editContent,
  duplicateAsDraft,
}: {
  content: InternalApiScreeningClearanceRuleContent;
  ruleHandle: string;
  entityType: InternalApiEntityType;
  index: number;
  onComplete: any;
  editContent: any;
  duplicateAsDraft: any;
}): React.JSX.Element => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const deleteContent = async (id: number) => {
    dispatch(
      showConfirmationModal({
        title: 'Are you sure?',
        confirmationText: 'Yes, delete it!',
        cancelText: 'No, keep it',
        onConfirm: async () => {
          try {
            await screeningClearanceRuleContentApi.deleteRuleContent({
              handle: ruleHandle,
              id: id,
            });
            await onComplete();
          } catch (_) {
            dispatch(
              showFeedback({
                message: 'Oops, something went wrong!',
                success: false,
              })
            );
          }
        },
      })
    );
  };

  const renderDropdownMenu = (status: InternalApiScreeningClearanceRuleContentStatusEnum) => {
    switch (status) {
      case InternalApiScreeningClearanceRuleContentStatusEnum.DRAFT:
        return (
          <Dropdown.Menu>
            <>
              <Button onClick={() => editContent(content)} className='dropdown-item' size='sm'>
                Edit
              </Button>
              <Button
                className='dropdown-item'
                size='sm'
                onClick={() => {
                  content.id && deleteContent(content.id);
                }}
              >
                Delete
              </Button>
              <Button onClick={() => duplicateAsDraft(content)} className='dropdown-item' size='sm'>
                Duplicate
              </Button>
            </>
          </Dropdown.Menu>
        );
      default:
        return (
          <Dropdown.Menu>
            <Button onClick={() => editContent(content)} className='dropdown-item' size='sm'>
              View
            </Button>
            <Button onClick={() => duplicateAsDraft(content)} className='dropdown-item' size='sm'>
              Duplicate as draft
            </Button>
          </Dropdown.Menu>
        );
    }
  };

  function navigateToClearances(contentId: number) {
    navigate(clearanceRuleClearancesDashboardLink(contentId, entityType));
  }

  return (
    <tr key={content.id} className={`custom-table-row-${index % 2}`}>
      <td>{content.id}</td>
      <td>{content.lastUpdatedTime && <FormattedDateTime date={content.lastUpdatedTime} showTime />}</td>
      <td>{content.lastUpdatedBy && content.lastUpdatedBy.name}</td>
      <td>{content.note}</td>
      <td className={content.status.toLowerCase()}>{formatEnumLabel(content.status)}</td>
      <td>
        {(content.status === InternalApiScreeningClearanceRuleContentStatusEnum.CURRENT ||
          content.status === InternalApiScreeningClearanceRuleContentStatusEnum.HISTORIC) &&
          content.id && (
            <Button
              className='btn-white'
              data-testid='view-clearances-button'
              size='sm'
              onClick={() => navigateToClearances(content.id!)}
            >
              View
            </Button>
          )}
      </td>
      <td>
        <Has
          anyPermissions={[CAN_MODIFY_SCREENING_CLEARANCE_RULE_CONTENT, CAN_DELETE_SCREENING_CLEARANCE_RULE_CONTENT]}
        >
          <DropdownWrapper>
            <Dropdown.Toggle
              data-testid='content-edit-dropdown'
              as={Button}
              className='btn-white'
              size='sm'
              id='content-edit-dropdown'
            >
              •••
            </Dropdown.Toggle>
            {renderDropdownMenu(content.status)}
          </DropdownWrapper>
        </Has>
      </td>
    </tr>
  );
};

export default ContentInfo;
